"use client";

import { Play } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function HotelHero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const proofPoints = [{
    label: "No long-term lock-in"
  }, {
    label: "Rapid revenue audit"
  }, {
    label: "Data-backed strategy"
  }];
  return <section data-section-id="1673" id="hoteldirectbookinghero" className="relative overflow-hidden bg-background py-28 md:py-40">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 hidden w-[55%] lg:block">
        <svg viewBox="0 0 800 900" preserveAspectRatio="none" className="h-full w-full opacity-30">
          <path d="M 120 80 C 60 220, 20 380, 90 540 C 160 700, 320 820, 500 840 C 680 860, 820 720, 800 540 C 780 360, 700 200, 540 110 C 400 30, 220 -20, 120 80 Z" fill="var(--accent)" />
        </svg>
      </div>

      {}
      <div aria-hidden="true" className="pointer-events-none absolute right-[20%] top-1/2 hidden -translate-y-1/2 select-none font-serif italic text-foreground/[0.07] lg:block" style={{
      fontSize: "clamp(180px, 22vw, 320px)",
      lineHeight: 1
    }}>
        H
      </div>

      <div ref={ref} className="relative mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:px-16">
        {}
        <div className={`relative z-10 lg:col-span-7 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          {}
          <div className="flex items-center gap-4">
            <span className="font-mono text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
              § 01 &mdash; Expert Revenue Management
            </span>
          </div>
          <div className="mt-3 flex items-center">
            <div className="h-px w-16 bg-border" />
            <span className="-ml-[3px] block h-[6px] w-[6px] rounded-lg bg-primary" />
          </div>

          {}
          <h1 className="font-default mt-10 font-serif text-foreground" style={{
          fontSize: "clamp(2.75rem, 5.4vw + 1rem, 5.5rem)",
          fontWeight: 360,
          letterSpacing: "-0.025em",
          lineHeight: 0.98
        }}>
            Maximize Your Hotel’s Revenue with{" "}
            <span className="font-serif italic" style={{
            color: "var(--chart-2)",
            fontWeight: 360
          }}>
              Data-Driven
            </span>{" "}
            OTA Strategies
          </h1>

          {}
          <p className="font-default mt-8 max-w-[80%] text-muted-foreground" style={{
          fontSize: "clamp(1.0625rem, 0.4vw + 0.9rem, 1.25rem)",
          fontWeight: 400,
          lineHeight: 1.62
        }}>
            HotelRevBaBa provides professional revenue management consulting to help hotel owners and investors optimize OTA listings, increase direct bookings, and maximize profitability.
          </p>

          {}
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Button asChild className="h-auto rounded-lg bg-primary px-7 py-3.5 text-sm font-medium tracking-[0.005em] text-primary-foreground shadow-sm transition-all duration-200 hover:bg-primary/90 hover:shadow-md">
              <Link to="/ContactPage">Book a Consultation</Link>
            </Button>
            <Button asChild variant="outline" className="h-auto rounded-lg border border-foreground bg-transparent px-7 py-3.5 text-sm font-medium text-foreground transition-all duration-200 hover:bg-foreground hover:text-background">
              <Link to="/OurServicesPage">
                <Play className="size-4" strokeWidth={1.5} />
                Explore Our Services
              </Link>
            </Button>
          </div>

          {}
          <ul className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4">
            {proofPoints.map((point, i) => <li data-index={i} key={point.label} className="flex items-center gap-2.5">
                <span className="block h-[5px] w-[5px] rounded-lg bg-primary" />
                <span className="font-mono text-[0.6875rem] font-normal uppercase tracking-[0.12em] text-muted-foreground">
                  {point.label}
                </span>
              </li>)}
          </ul>
        </div>

        {}
        <div className={`relative lg:col-span-5 transition-all duration-700 ease-out delay-150 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          <div className="group relative mx-auto aspect-[5/6] w-full max-w-[520px]">
            {}
            <div aria-hidden="true" className="pointer-events-none absolute -right-4 -top-8 select-none font-serif italic text-foreground/[0.07] lg:hidden" style={{
            fontSize: "180px",
            lineHeight: 1
          }}>
              H
            </div>

            {}
            <div className="absolute inset-0">
              <svg viewBox="0 0 500 600" className="absolute inset-0 h-full w-full" aria-hidden="true">
                <defs>
                  <clipPath id="flowstate-pebble-clip" clipPathUnits="userSpaceOnUse">
                    <path d="M 80 60 C 30 140, 10 260, 30 380 C 50 500, 140 580, 270 590 C 400 600, 470 510, 480 380 C 490 250, 460 130, 360 60 C 270 -10, 150 -10, 80 60 Z" />
                  </clipPath>
                </defs>
                <path d="M 80 60 C 30 140, 10 260, 30 380 C 50 500, 140 580, 270 590 C 400 600, 470 510, 480 380 C 490 250, 460 130, 360 60 C 270 -10, 150 -10, 80 60 Z" fill="var(--secondary)" fillOpacity="0.7" />
              </svg>

              {}
              <div className="absolute inset-0 overflow-hidden" style={{
              clipPath: "url(#flowstate-pebble-clip)"
            }}>
                <div className="relative h-full w-full">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_strategy.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_strategy.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_strategy.png-wvc-sizes" alt="Sunil Singh reviewing hotel revenue analytics" loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.025]" />
                  <div aria-hidden="true" className="absolute inset-0 bg-background/10 mix-blend-screen" />
                </div>
              </div>
            </div>

            {}
            <div className="absolute -bottom-2 left-4 z-10 flex items-center gap-2">
              <span className="block h-[5px] w-[5px] rounded-lg bg-primary" />
              <span className="font-mono text-[0.6875rem] font-normal uppercase tracking-[0.12em] text-muted-foreground">
                Revenue optimization expert
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}